import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { apiClient } from '@/lib/api-client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { BarChart3, Users, Heart, CheckCircle, TrendingUp, Clock } from 'lucide-react';

interface FunnelStep {
  step: string;
  label: string;
  count: number;
  width?: number;
}

interface FunnelData {
  funnel: FunnelStep[];
  conversions: Record<string, number>;
  totalSignups: number;
  totalMutualMatches: number;
  avgHoursToProfileComplete: number | null;
  avgDaysToFirstMatch: number | null;
}

interface TrendPoint {
  date: string;
  count: number;
}

const STEP_ICONS: Record<string, React.ReactNode> = {
  SIGNUP: <Users className="w-5 h-5" />,
  PROFILE_COMPLETE: <CheckCircle className="w-5 h-5" />,
  FIRST_INTEREST: <Heart className="w-5 h-5" />,
  FIRST_MATCH: <TrendingUp className="w-5 h-5" />,
  MUTUAL_MATCH: <Heart className="w-5 h-5 text-rose-500" />,
};

const STEP_COLORS: Record<string, string> = {
  SIGNUP: 'bg-blue-500',
  PROFILE_COMPLETE: 'bg-indigo-500',
  FIRST_INTEREST: 'bg-violet-500',
  FIRST_MATCH: 'bg-pink-500',
  MUTUAL_MATCH: 'bg-rose-500',
};

const AnalyticsDashboardPage: React.FC = () => {
  const [funnel, setFunnel] = useState<FunnelData | null>(null);
  const [trend, setTrend] = useState<TrendPoint[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [funnelRes, trendRes] = await Promise.all([
          apiClient.get<FunnelData>('/api/analytics/funnel'),
          apiClient.get<TrendPoint[]>('/api/analytics/funnel/trend?days=30'),
        ]);
        setFunnel(funnelRes.data);
        setTrend(trendRes.data);
      } catch {
        setError('Failed to load analytics data.');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto p-6 space-y-6">
        <Skeleton className="h-10 w-64" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-32" />
          ))}
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  if (error || !funnel) {
    return (
      <div className="max-w-6xl mx-auto p-6">
        <Card className="text-center py-16">
          <p className="text-gray-500">{error || 'No data available.'}</p>
        </Card>
      </div>
    );
  }

  const maxCount = Math.max(...funnel.funnel.map((s) => s.count), 1);

  const statCards = [
    { label: 'Total Signups', value: funnel.totalSignups, icon: <Users className="w-5 h-5" />, color: 'text-blue-600' },
    { label: 'Profile Complete', value: funnel.funnel[1]?.count ?? 0, icon: <CheckCircle className="w-5 h-5" />, color: 'text-indigo-600' },
    { label: 'Mutual Matches', value: funnel.totalMutualMatches, icon: <Heart className="w-5 h-5" />, color: 'text-rose-600' },
    { label: 'Overall Rate', value: `${funnel.conversions.OVERALL_SIGNUP_TO_MATCH ?? 0}%`, icon: <TrendingUp className="w-5 h-5" />, color: 'text-emerald-600' },
    { label: 'Avg Profile Time', value: funnel.avgHoursToProfileComplete ? `${funnel.avgHoursToProfileComplete}h` : 'N/A', icon: <Clock className="w-5 h-5" />, color: 'text-amber-600' },
    { label: 'Avg Days to Match', value: funnel.avgDaysToFirstMatch ? `${funnel.avgDaysToFirstMatch}d` : 'N/A', icon: <Clock className="w-5 h-5" />, color: 'text-purple-600' },
  ];

  return (
    <>
      <Helmet>
        <title>Analytics Dashboard | Classifieds</title>
      </Helmet>

      <div className="max-w-6xl mx-auto p-6 space-y-8">
        <div className="flex items-center gap-3">
          <BarChart3 className="w-8 h-8 text-rose-600" />
          <h1 className="text-2xl font-bold text-gray-900">Analytics Dashboard</h1>
        </div>

        {/* Stat Cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {statCards.map((card) => (
            <Card key={card.label} className="hover:shadow-md transition-shadow">
              <CardContent className="p-4">
                <div className={`mb-2 ${card.color}`}>{card.icon}</div>
                <div className="text-2xl font-bold text-gray-900">{card.value}</div>
                <div className="text-xs text-gray-500 mt-1">{card.label}</div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Funnel Visualization */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Conversion Funnel</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {funnel.funnel.map((step, idx) => {
                const widthPct = maxCount > 0 ? (step.count / maxCount) * 100 : 0;
                const dropRate =
                  idx > 0 && funnel.funnel[idx - 1].count > 0
                    ? ((1 - step.count / funnel.funnel[idx - 1].count) * 100).toFixed(0)
                    : null;

                return (
                  <div key={step.step} className="flex items-center gap-4">
                    {/* Label */}
                    <div className="w-36 flex items-center gap-2 shrink-0">
                      <span className={idx === funnel.funnel.length - 1 ? 'text-rose-500' : 'text-gray-500'}>
                        {STEP_ICONS[step.step]}
                      </span>
                      <span className="text-sm font-medium text-gray-700">{step.label}</span>
                    </div>

                    {/* Bar */}
                    <div className="flex-1 relative">
                      <div className="h-9 rounded-lg bg-gray-100 overflow-hidden">
                        <div
                          className={`h-full rounded-lg transition-all duration-700 ${STEP_COLORS[step.step]}`}
                          style={{ width: `${Math.max(widthPct, 2)}%` }}
                        />
                      </div>
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-bold text-gray-700">
                        {step.count.toLocaleString()}
                      </span>
                    </div>

                    {/* Drop rate */}
                    <div className="w-20 text-right">
                      {dropRate && (
                        <span className="text-xs text-red-500">
                          -{dropRate}%
                        </span>
                      )}
                      {idx === 0 && (
                        <span className="text-xs text-gray-400">100%</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Conversion Summary */}
            <div className="mt-8 pt-6 border-t grid grid-cols-2 md:grid-cols-4 gap-4">
              {Object.entries(funnel.conversions)
                .filter(([k]) => k.endsWith('_RATE') && k !== 'SIGNUP_RATE')
                .map(([key, value]) => (
                  <div key={key} className="text-center p-3 bg-gray-50 rounded-lg">
                    <div className="text-xl font-bold text-gray-900">{value}%</div>
                    <div className="text-xs text-gray-500 mt-1">
                      {key.replace(/_RATE$/, '').replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())}
                    </div>
                  </div>
                ))}
            </div>
          </CardContent>
        </Card>

        {/* Daily Signup Trend */}
        {trend.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Daily Signups (Last 30 Days)</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-48 flex items-end gap-1">
                {trend.map((point) => {
                  const maxTrend = Math.max(...trend.map((t) => t.count), 1);
                  const heightPct = (point.count / maxTrend) * 100;
                  return (
                    <div
                      key={point.date}
                      className="flex-1 flex flex-col items-center justify-end group relative"
                    >
                      <div
                        className="w-full bg-rose-400 hover:bg-rose-500 rounded-t-sm transition-colors min-h-[2px]"
                        style={{ height: `${Math.max(heightPct, 1)}%` }}
                        title={`${point.date}: ${point.count}`}
                      />
                      {/* Tooltip on hover */}
                      <div className="absolute bottom-full mb-1 hidden group-hover:block bg-gray-800 text-white text-xs rounded px-2 py-1 whitespace-nowrap z-10">
                        {point.date}: {point.count}
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="flex justify-between mt-2 text-xs text-gray-400">
                <span>{trend[0]?.date}</span>
                <span>{trend[trend.length - 1]?.date}</span>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </>
  );
};

export default AnalyticsDashboardPage;
